<script lang="ts" setup>
	import IMask, { InputMask, type FactoryArg } from "imask";
	import { alphabetAnyCaseNumbersRegex } from "~/utils/validators";
	import { type AutocompleteOptions, type InputTypes } from "~/utils/types";

	const props = withDefaults(
		defineProps<{
			id?: string;
			name?: string;
			value: string | number;
			type: InputTypes;
			autocomplete?: AutocompleteOptions;
			maxlength?: number;
			disabled?: boolean;
			readonly?: boolean;
			placeholder?: string;
			min?: number;
			max?: number;
			step?: number;
			formatThousands?: boolean;
		}>(),
		{
			autocomplete: "off",
			multiline: false,
			min: undefined,
			max: undefined,
			step: undefined,
			formatThousands: false,
		}
	);

	const emit = defineEmits<{
		(e: "update:value", value: string | number): void;
		(e: "change", value: string | number): void;
		(e: "focusin"): void;
		(e: "focusout"): void;
		(e: "enterpressed"): void;
	}>();

	const element = ref<HTMLInputElement | HTMLTextAreaElement | undefined>();

	const value = computed({
		get() {
			return props.value;
		},
		set(value: string) {
			emit("update:value", value);
		},
	});

	const type = computed(() => {
		if (props.formatThousands) {
			return "text";
		}
		if (["text", "password", "email", "number"].includes(props.type)) {
			return props.type;
		} else {
			return "text";
		}
	});

	const instance = ref<InputMask<any>>();

	const formatWithThousandsSeparators = (input: string | number): string => {
		const digits = String(input ?? "").replace(/\D/g, "");
		return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	};

	const formattedValue = computed(() => formatWithThousandsSeparators(props.value));

	const formattedInput = (event: Event) => {
		const input = event.target as HTMLInputElement;
		const caret = input.selectionStart ?? input.value.length;
		const digitsBeforeCaret = input.value.slice(0, caret).replace(/\D/g, "").length;
		const digits = input.value.replace(/\D/g, "");
		const formatted = formatWithThousandsSeparators(digits);

		input.value = formatted;
		emit("update:value", props.type === "number" && digits ? Number(digits) : digits);

		let nextCaret = 0;
		let digitsSeen = 0;
		while (nextCaret < formatted.length && digitsSeen < digitsBeforeCaret) {
			if (/\d/.test(formatted[nextCaret])) {
				digitsSeen += 1;
			}
			nextCaret += 1;
		}
		input.setSelectionRange(nextCaret, nextCaret);
	};

	const mask = computed(() => {
		if (props.type === "email") {
			return {
				prepare: function (string: string) {
					return string.toLowerCase();
				},
				mask: /^([A-Za-z]|[0-9]|\.|\-|\_|\+|\@)+$/,
			} as FactoryArg;
		} else if (props.type === "iban-number" || props.type === "swift-number") {
			return {
				prepare: function (string: string) {
					return string.toUpperCase();
				},
				mask: alphabetAnyCaseNumbersRegex,
			} as FactoryArg;
		} else if (props.type === "aba-number" || props.type === "zip") {
			return {
				mask: /^[0-9]*$/,
			} as FactoryArg;
		} else if (props.type === "url") {
			return {
				// regex for anything but spaces
				mask: /^[^\s]*$/,
			} as FactoryArg;
		}
	});

	const maxlength = computed(() => {
		if (props.type === "email") {
			return 254;
		} else if (props.type === "iban-number") {
			return 34;
		} else if (props.type === "swift-number") {
			return 11;
		} else if (props.type === "aba-number") {
			return 9;
		} else if (props.type === "zip") {
			return 5;
		} else if (props.type === "url") {
			return 2048;
		} else {
			return props.maxlength;
		}
	});

	onMounted(function () {
		watch(
			[mask, element],
			() => {
				if (mask.value && element.value) {
					instance.value = IMask(element.value, mask.value);
					//const { unmaskedValue, typedValue, value } = useIMask(element, mask);
				} else if (instance.value) {
					instance.value.destroy();
				}
			},
			{ immediate: true }
		);
	});

	onBeforeUnmount(function () {
		if (instance.value) {
			instance.value.destroy();
		}
	});
</script>

<template>
	<input
		v-if="formatThousands"
		:placeholder="placeholder"
		:id="props.id"
		:type="type"
		:name="name"
		:autocomplete="autocomplete"
		:maxlength="maxlength"
		ref="element"
		:value="formattedValue"
		@input="formattedInput"
		@change="() => $emit('change', value)"
		@focusin="() => $emit('focusin')"
		@focusout="() => $emit('focusout')"
		:disabled="props.disabled"
		:readonly="props.readonly"
		@keyup.enter="() => emit('enterpressed')"
		inputmode="numeric"
		pattern="[0-9,]*"
	/>
	<input
		v-else
		:placeholder="placeholder"
		:id="props.id"
		:type="type"
		:name="name"
		:autocomplete="autocomplete"
		:maxlength="maxlength"
		ref="element"
		v-model="value"
		@change="() => $emit('change', value)"
		@focusin="() => $emit('focusin')"
		@focusout="() => $emit('focusout')"
		:disabled="props.disabled"
		:readonly="props.readonly"
		@keyup.enter="() => emit('enterpressed')"
		:min="props.min"
		:max="props.max"
		:step="props.step"
	/>
</template>

<style scoped lang="scss"></style>
